import { ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { COLORS } from '../../constants'
import { Project } from '../../utils/resumeData'
import Tag from './Tag'
import TvButton from './TvButton'

type ProjectCardProps = {
    project: Project
    /** Horizontal puts the screenshot beside the text on desktop (used for the homepage feature) */
    layout?: 'vertical' | 'horizontal'
    /** Overrides the project.kind label */
    eyebrow?: string
    /** Extra buttons shown next to "Visit site" */
    actions?: ReactNode
    /** Uses project.summary and hides the tech tags (homepage) */
    compact?: boolean
}

/** Screenshot in a minimal browser frame, linking out to the live site. */
function ProjectCard({ project, layout = 'vertical', eyebrow, actions, compact = false }: ProjectCardProps) {
    const host = project.url.replace(/^https?:\/\/(www\.)?/, '')
    const horizontal = layout === 'horizontal'
    return (
        <Box
            component="article"
            sx={{
                display: horizontal ? 'grid' : 'flex',
                flexDirection: 'column',
                gridTemplateColumns: horizontal ? { xs: '1fr', md: '1.15fr 1fr' } : undefined,
                borderRadius: 3, overflow: 'hidden', backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}`,
            }}
        >
            <Box
                component="a"
                href={project.url}
                target="_blank"
                rel="noreferrer"
                aria-label={`Visit ${project.name}`}
                sx={{
                    display: 'flex', alignItems: horizontal ? 'center' : 'flex-end', backgroundColor: COLORS.black,
                    p: { xs: 1.5, sm: 2.5 }, pb: horizontal ? { xs: 0, md: 2.5 } : 0,
                    textDecoration: 'none', '&:hover img': { transform: 'translateY(-4px)' },
                }}
            >
                <Box sx={{ width: '100%', borderRadius: horizontal ? { xs: '10px 10px 0 0', md: '10px' } : '10px 10px 0 0', overflow: 'hidden', backgroundColor: COLORS.white }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, height: 30, backgroundColor: COLORS.blackSecondary, borderBottom: `1px solid ${COLORS.blackBorder}` }}>
                        {[0, 1, 2].map((i) => <Box key={i} sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#3A3F4A' }} />)}
                        <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: COLORS.mutedOnDark, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{host}</Typography>
                    </Box>
                    {/* Light placeholder so the frame doesn't look broken before the lazy image loads */}
                    <Box sx={{ overflow: 'hidden', aspectRatio: '16 / 10', backgroundColor: COLORS.light }}>
                        <Box
                            component="img"
                            src={project.image}
                            alt={`Screenshot of ${project.name}`}
                            loading="lazy"
                            width={1200}
                            height={750}
                            sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 200ms ease' }}
                        />
                    </Box>
                </Box>
            </Box>
            <Box sx={{ p: { xs: 3, md: horizontal ? 4 : 3.5 }, display: 'flex', flexDirection: 'column', justifyContent: horizontal ? 'center' : undefined, gap: 1.5, flexGrow: 1 }}>
                <Typography variant="overline" component="p" sx={{ color: COLORS.accentOnLight }}>{eyebrow ?? project.kind}</Typography>
                <Typography variant="h3" sx={{ mt: -1 }}>{project.name}</Typography>
                <Typography sx={{ color: 'text.secondary', flexGrow: horizontal ? 0 : 1 }}>{compact ? project.summary ?? project.description : project.description}</Typography>
                {!compact && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {project.tags.map((t) => <Tag key={t} text={t} />)}
                    </Box>
                )}
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    <TvButton text="Visit site" link={project.url} variant="outline" size="small" icon={<OpenInNewIcon fontSize="small" />} />
                    {actions}
                </Box>
            </Box>
        </Box>
    )
}

export default ProjectCard
